<template>
  <div class="min-h-screen bg-white">
    <!-- Hero Section -->
    <section class="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center">
          <h1 class="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            {{ $t('api.title') }}
          </h1>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            {{ $t('api.subtitle') }}
          </p>
        </div>
      </div>
    </section>

    <!-- Documentation principale -->
    <section class="py-20 bg-white">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Endpoint GET /public/schemas -->
        <div class="bg-white rounded-lg border border-gray-200 overflow-hidden mb-8">
          <button
            class="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
            @click="expandedRoutes.schemas = !expandedRoutes.schemas"
          >
            <div class="flex items-center space-x-3">
              <span class="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">GET</span>
              <code class="text-sm font-mono text-gray-900">{{ $t('api.endpoint.path') }}</code>
              <span class="text-gray-700" v-html="$t('api.endpoint.description')"></span>
            </div>
            <ChevronDownIcon 
              class="w-5 h-5 text-gray-500 transition-transform"
              :class="{ 'transform rotate-180': expandedRoutes.schemas }"
            />
          </button>
          <div v-show="expandedRoutes.schemas" class="px-4 pb-4 border-t border-gray-200">
            <!-- Description -->
            <div class="mb-6 mt-4">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">Description</h3>
              <p class="text-gray-700 leading-relaxed">
                {{ $t('api.endpoint.fullDescription') }}
              </p>
            </div>

            <!-- Paramètres -->
            <div class="mb-6">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">{{ $t('api.endpoint.parameters.title') }}</h3>
              <p class="text-gray-600 mb-2">{{ $t('api.endpoint.parameters.none') }}</p>
              <div class="bg-gray-50 rounded-lg p-4">
                <p class="text-sm text-gray-600">
                  <strong>{{ $t('api.endpoint.parameters.note') }}</strong>
                </p>
              </div>
            </div>

            <!-- Exemple de requête -->
            <div class="mb-6">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">{{ $t('api.endpoint.request.title') }}</h3>
              <div class="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre class="text-sm text-green-400 font-mono"><code>curl -X GET "{{ apiBaseUrl }}/public/schemas" \
  -H "Accept: application/json"</code></pre>
              </div>
            </div>

            <!-- Réponse -->
            <div class="mb-6">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">{{ $t('api.endpoint.response.title') }}</h3>
              <p class="text-gray-700 mb-3">
                {{ $t('api.endpoint.response.description') }}
              </p>
              <ul class="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li><code class="bg-gray-100 px-2 py-1 rounded">uuid</code> : {{ $t('api.endpoint.response.fields.uuid') }}</li>
                <li><code class="bg-gray-100 px-2 py-1 rounded">appellation</code> : {{ $t('api.endpoint.response.fields.appellation') }}</li>
                <li><code class="bg-gray-100 px-2 py-1 rounded">type</code> : {{ $t('api.endpoint.response.fields.type') }}</li>
                <li><code class="bg-gray-100 px-2 py-1 rounded">description</code> : {{ $t('api.endpoint.response.fields.description') }}</li>
                <li><code class="bg-gray-100 px-2 py-1 rounded">version</code> : {{ $t('api.endpoint.response.fields.version') }}</li>
                <li><code class="bg-gray-100 px-2 py-1 rounded">section_list</code> : {{ $t('api.endpoint.response.fields.section_list') }}</li>
              </ul>
              
              <div class="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre class="text-sm text-gray-300 font-mono"><code>[
  {
    "uuid": "abc12345",
    "appellation": "Schéma d'acquisition standard",
    "type": "acquisition",
    "description": "Schéma standard pour les acquisitions",
    "version": "2025-01-27T10:30:00.000Z",
    "section_list": [
      {
        "id": "how",
        "type": "text",
        "translations": {
          "fr": {
            "titre": "Comment ?",
            "description": "Méthode d'acquisition"
          }
        },
        "conditions": [
          {
            "condition_id": "what",
            "condition_value": "acquisition"
          }
        ]
      }
    ]
  }
]</code></pre>
              </div>
            </div>

            <!-- Codes de statut -->
            <div class="mb-6">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">{{ $t('api.endpoint.statusCodes.title') }}</h3>
              <div class="space-y-2">
                <div class="flex items-center space-x-3">
                  <span class="px-3 py-1 bg-green-100 text-green-800 text-sm font-semibold rounded">200</span>
                  <span class="text-gray-700">{{ $t('api.endpoint.statusCodes.200') }}</span>
                </div>
                <div class="flex items-center space-x-3">
                  <span class="px-3 py-1 bg-red-100 text-red-800 text-sm font-semibold rounded">404</span>
                  <span class="text-gray-700">{{ $t('api.endpoint.statusCodes.404') }}</span>
                </div>
                <div class="flex items-center space-x-3">
                  <span class="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-semibold rounded">429</span>
                  <span class="text-gray-700">{{ $t('api.endpoint.statusCodes.429') }}</span>
                </div>
              </div>
            </div>

            <!-- Utilisation -->
            <div class="mb-6">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">{{ $t('api.endpoint.useCases.title') }}</h3>
              <ul class="list-disc list-outside text-gray-700 space-y-2 ml-6">
                <li v-for="(item, index) in useCasesItems" :key="index" class="leading-relaxed">{{ item }}</li>
              </ul>
            </div>

            <!-- Tester l'API -->
            <div class="mt-8 pt-6 border-t border-gray-200">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">{{ $t('api.endpoint.test.title') }}</h3>
              <button
                :disabled="isLoading"
                class="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                @click="testApi"
              >
                <span v-if="!isLoading">{{ $t('api.endpoint.test.button') }}</span>
                <span v-else class="flex items-center">
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {{ $t('api.endpoint.test.loading') }}
                </span>
              </button>
              
              <div v-if="testResult" class="mt-4">
                <div v-if="testResult.success" class="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p class="text-green-800 font-medium mb-2">✅ {{ $t('api.endpoint.test.success') }}</p>
                  <p class="text-sm text-green-700">
                    {{ testResult.count }} {{ $t('api.endpoint.test.count') }}
                  </p>
                </div>
                <div v-else class="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p class="text-red-800 font-medium mb-2">❌ {{ $t('api.endpoint.test.error') }}</p>
                  <p class="text-sm text-red-700">{{ testResult.error }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Endpoint GET /export-mappings/active -->
        <div class="bg-white rounded-lg border border-gray-200 overflow-hidden mb-8">
          <button
            class="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
            @click="expandedRoutes.exportMappings = !expandedRoutes.exportMappings"
          >
            <div class="flex items-center space-x-3">
              <span class="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">GET</span>
              <code class="text-sm font-mono text-gray-900">/export-mappings/active</code>
              <span class="text-gray-700">Liste publique des mappings d'export actifs</span>
            </div>
            <ChevronDownIcon
              class="w-5 h-5 text-gray-500 transition-transform"
              :class="{ 'transform rotate-180': expandedRoutes.exportMappings }"
            />
          </button>
          <div v-show="expandedRoutes.exportMappings" class="px-4 pb-4 border-t border-gray-200">
            <!-- Description -->
            <div class="mb-6 mt-4">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">Description</h3>
              <p class="text-gray-700 leading-relaxed">
                Retourne la liste de tous les <strong>mappings d'export actifs</strong> (is_active=true), accessibles
                publiquement. La réponse inclut aussi <code class="bg-gray-100 px-2 py-1 rounded">field_mappings</code>
                et <code class="bg-gray-100 px-2 py-1 rounded">export_options</code>.
              </p>
            </div>

            <!-- Paramètres -->
            <div class="mb-6">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">Paramètres</h3>
              <div class="bg-gray-50 rounded-lg p-4 space-y-2 text-sm text-gray-700">
                <div>
                  <code class="bg-gray-100 px-2 py-1 rounded">skip</code> (query, optionnel) : offset de pagination (défaut 0)
                </div>
                <div>
                  <code class="bg-gray-100 px-2 py-1 rounded">limit</code> (query, optionnel) : taille de page (défaut 100)
                </div>
              </div>
            </div>

            <!-- Exemple de requête -->
            <div class="mb-6">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">Exemple de requête</h3>
              <div class="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre class="text-sm text-green-400 font-mono"><code>curl -X GET "{{ apiBaseUrl }}/export-mappings/active?skip=0&amp;limit=100" \
  -H "Accept: application/json"</code></pre>
              </div>
            </div>

            <!-- Réponse -->
            <div class="mb-6">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">Réponse</h3>
              <p class="text-gray-700 mb-3">
                Tableau de mappings. Champs principaux :
              </p>
              <ul class="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li><code class="bg-gray-100 px-2 py-1 rounded">uuid</code> : identifiant du mapping</li>
                <li><code class="bg-gray-100 px-2 py-1 rounded">name</code> : nom du mapping</li>
                <li><code class="bg-gray-100 px-2 py-1 rounded">format_type</code> : <code class="bg-gray-100 px-2 py-1 rounded">json</code> / <code class="bg-gray-100 px-2 py-1 rounded">csv</code> / <code class="bg-gray-100 px-2 py-1 rounded">xml</code></li>
                <li><code class="bg-gray-100 px-2 py-1 rounded">is_active</code> : actif</li>
                <li><code class="bg-gray-100 px-2 py-1 rounded">field_mappings</code> : liste des champs exportés</li>
                <li><code class="bg-gray-100 px-2 py-1 rounded">export_options</code> : options d'export (ex: <code class="bg-gray-100 px-2 py-1 rounded">qr_code_optimized</code>)</li>
              </ul>

              <div class="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre class="text-sm text-gray-300 font-mono"><code>[
  {
    "uuid": "e2c1c0c2-1111-2222-3333-444444444444",
    "name": "QrCode_V1",
    "description": "Mapping JSON compact pour QR code",
    "format_type": "json",
    "is_active": true,
    "is_template": false,
    "created_by": "USR00001",
    "created_at": "2025-12-15T10:00:00Z",
    "updated_at": "2025-12-15T10:00:00Z",
    "field_mappings": [
      {
        "id": 1,
        "export_mapping_uuid": "e2c1c0c2-1111-2222-3333-444444444444",
        "source_entity": "memo",
        "source_field": "title",
        "destination_path": "Activity.Title",
        "field_label": "Titre",
        "field_description": "Titre du mémo",
        "static_value": null,
        "transformation_rule": null,
        "ordre": 0,
        "created_at": "2025-12-15T10:00:00Z",
        "updated_at": "2025-12-15T10:00:00Z"
      }
    ],
    "export_options": [
      {
        "id": 10,
        "export_mapping_uuid": "e2c1c0c2-1111-2222-3333-444444444444",
        "option_key": "qr_code_optimized",
        "option_value": "true",
        "created_at": "2025-12-15T10:00:00Z",
        "updated_at": "2025-12-15T10:00:00Z"
      }
    ]
  }
]</code></pre>
              </div>
            </div>

            <!-- Codes de statut -->
            <div class="mb-6">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">Codes de statut</h3>
              <div class="space-y-2">
                <div class="flex items-center space-x-3">
                  <span class="px-3 py-1 bg-green-100 text-green-800 text-sm font-semibold rounded">200</span>
                  <span class="text-gray-700">Succès</span>
                </div>
                <div class="flex items-center space-x-3">
                  <span class="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-semibold rounded">422</span>
                  <span class="text-gray-700">Paramètres invalides</span>
                </div>
                <div class="flex items-center space-x-3">
                  <span class="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-semibold rounded">429</span>
                  <span class="text-gray-700">Trop de requêtes (rate limit)</span>
                </div>
              </div>
            </div>

            <!-- Utilisation -->
            <div class="mb-6">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">Utilisation</h3>
              <ul class="list-disc list-outside text-gray-700 space-y-2 ml-6">
                <li class="leading-relaxed">Afficher les <strong>mappings disponibles</strong> dans l'UI d'export.</li>
                <li class="leading-relaxed">Récupérer les options (ex: <code class="bg-gray-100 px-2 py-1 rounded">qr_code_optimized</code>) pour adapter l'export (fichier vs QR code).</li>
              </ul>
            </div>

            <!-- Tester l'API -->
            <div class="mt-8 pt-6 border-t border-gray-200">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Tester l'API</h3>
              <button
                :disabled="isLoadingExportMappings"
                class="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                @click="testExportMappingsApi"
              >
                <span v-if="!isLoadingExportMappings">Tester GET /export-mappings/active</span>
                <span v-else class="flex items-center">
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Test en cours...
                </span>
              </button>

              <div v-if="exportMappingsTestResult" class="mt-4">
                <div v-if="exportMappingsTestResult.success" class="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p class="text-green-800 font-medium mb-2">✅ Succès</p>
                  <p class="text-sm text-green-700">
                    {{ exportMappingsTestResult.count }} mapping(s) actif(s)
                  </p>
                </div>
                <div v-else class="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p class="text-red-800 font-medium mb-2">❌ Erreur</p>
                  <p class="text-sm text-red-700">{{ exportMappingsTestResult.error }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Autres routes publiques -->
        <div class="bg-gray-50 rounded-xl p-8">
          <h2 class="text-2xl font-bold text-gray-900 mb-4">{{ $t('api.otherRoutes.title') }}</h2>
          <p class="text-gray-600 mb-4">
            {{ $t('api.otherRoutes.description') }}
          </p>
          
          <!-- Liste des routes avec accordéon -->
          <div class="space-y-3">
            <!-- Tags -->
            <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                class="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                @click="expandedRoutes.tags = !expandedRoutes.tags"
              >
                <div class="flex items-center space-x-3">
                  <span class="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">GET</span>
                  <code class="text-sm font-mono text-gray-900">{{ $t('api.otherRoutes.tags.path') }}</code>
                  <span class="text-gray-700">{{ $t('api.otherRoutes.tags.title') }}</span>
                </div>
                <ChevronDownIcon 
                  class="w-5 h-5 text-gray-500 transition-transform"
                  :class="{ 'transform rotate-180': expandedRoutes.tags }"
                />
              </button>
              <div v-show="expandedRoutes.tags" class="px-4 pb-4 border-t border-gray-200">
                <p class="text-gray-700 mt-3 mb-3">{{ $t('api.otherRoutes.tags.description') }}</p>
                <div class="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span class="font-medium text-gray-600">Rate limit:</span>
                    <span class="ml-2 text-gray-700">{{ $t('api.otherRoutes.tags.rateLimit') }}</span>
                  </div>
                  <div>
                    <span class="font-medium text-gray-600">Cache:</span>
                    <span class="ml-2 text-gray-700">{{ $t('api.otherRoutes.tags.cache') }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Mémos -->
            <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                class="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                @click="expandedRoutes.memos = !expandedRoutes.memos"
              >
                <div class="flex items-center space-x-3">
                  <span class="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">GET</span>
                  <code class="text-sm font-mono text-gray-900">{{ $t('api.otherRoutes.memos.path') }}</code>
                  <span class="text-gray-700">{{ $t('api.otherRoutes.memos.title') }}</span>
                </div>
                <ChevronDownIcon 
                  class="w-5 h-5 text-gray-500 transition-transform"
                  :class="{ 'transform rotate-180': expandedRoutes.memos }"
                />
              </button>
              <div v-show="expandedRoutes.memos" class="px-4 pb-4 border-t border-gray-200">
                <p class="text-gray-700 mt-3 mb-3">{{ $t('api.otherRoutes.memos.description') }}</p>
                <div class="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span class="font-medium text-gray-600">Rate limit:</span>
                    <span class="ml-2 text-gray-700">{{ $t('api.otherRoutes.memos.rateLimit') }}</span>
                  </div>
                  <div>
                    <span class="font-medium text-gray-600">Cache:</span>
                    <span class="ml-2 text-gray-700">{{ $t('api.otherRoutes.memos.cache') }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Agents -->
            <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                class="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                @click="expandedRoutes.agents = !expandedRoutes.agents"
              >
                <div class="flex items-center space-x-3">
                  <span class="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">GET</span>
                  <code class="text-sm font-mono text-gray-900">{{ $t('api.otherRoutes.agents.path') }}</code>
                  <span class="text-gray-700">{{ $t('api.otherRoutes.agents.title') }}</span>
                </div>
                <ChevronDownIcon 
                  class="w-5 h-5 text-gray-500 transition-transform"
                  :class="{ 'transform rotate-180': expandedRoutes.agents }"
                />
              </button>
              <div v-show="expandedRoutes.agents" class="px-4 pb-4 border-t border-gray-200">
                <p class="text-gray-700 mt-3 mb-3">{{ $t('api.otherRoutes.agents.description') }}</p>
                <div class="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span class="font-medium text-gray-600">Rate limit:</span>
                    <span class="ml-2 text-gray-700">{{ $t('api.otherRoutes.agents.rateLimit') }}</span>
                  </div>
                  <div>
                    <span class="font-medium text-gray-600">Cache:</span>
                    <span class="ml-2 text-gray-700">{{ $t('api.otherRoutes.agents.cache') }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Locations -->
            <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                class="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                @click="expandedRoutes.locations = !expandedRoutes.locations"
              >
                <div class="flex items-center space-x-3">
                  <span class="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">GET</span>
                  <code class="text-sm font-mono text-gray-900">{{ $t('api.otherRoutes.locations.path') }}</code>
                  <span class="text-gray-700">{{ $t('api.otherRoutes.locations.title') }}</span>
                </div>
                <ChevronDownIcon 
                  class="w-5 h-5 text-gray-500 transition-transform"
                  :class="{ 'transform rotate-180': expandedRoutes.locations }"
                />
              </button>
              <div v-show="expandedRoutes.locations" class="px-4 pb-4 border-t border-gray-200">
                <p class="text-gray-700 mt-3 mb-3">{{ $t('api.otherRoutes.locations.description') }}</p>
                <div class="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span class="font-medium text-gray-600">Rate limit:</span>
                    <span class="ml-2 text-gray-700">{{ $t('api.otherRoutes.locations.rateLimit') }}</span>
                  </div>
                  <div>
                    <span class="font-medium text-gray-600">Cache:</span>
                    <span class="ml-2 text-gray-700">{{ $t('api.otherRoutes.locations.cache') }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Material Entities -->
            <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                class="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                @click="expandedRoutes.materialEntities = !expandedRoutes.materialEntities"
              >
                <div class="flex items-center space-x-3">
                  <span class="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">GET</span>
                  <code class="text-sm font-mono text-gray-900">{{ $t('api.otherRoutes.materialEntities.path') }}</code>
                  <span class="text-gray-700">{{ $t('api.otherRoutes.materialEntities.title') }}</span>
                </div>
                <ChevronDownIcon 
                  class="w-5 h-5 text-gray-500 transition-transform"
                  :class="{ 'transform rotate-180': expandedRoutes.materialEntities }"
                />
              </button>
              <div v-show="expandedRoutes.materialEntities" class="px-4 pb-4 border-t border-gray-200">
                <p class="text-gray-700 mt-3 mb-3">{{ $t('api.otherRoutes.materialEntities.description') }}</p>
                <div class="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span class="font-medium text-gray-600">Rate limit:</span>
                    <span class="ml-2 text-gray-700">{{ $t('api.otherRoutes.materialEntities.rateLimit') }}</span>
                  </div>
                  <div>
                    <span class="font-medium text-gray-600">Cache:</span>
                    <span class="ml-2 text-gray-700">{{ $t('api.otherRoutes.materialEntities.cache') }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Digital Entities -->
            <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                class="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                @click="expandedRoutes.digitalEntities = !expandedRoutes.digitalEntities"
              >
                <div class="flex items-center space-x-3">
                  <span class="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">GET</span>
                  <code class="text-sm font-mono text-gray-900">{{ $t('api.otherRoutes.digitalEntities.path') }}</code>
                  <span class="text-gray-700">{{ $t('api.otherRoutes.digitalEntities.title') }}</span>
                </div>
                <ChevronDownIcon 
                  class="w-5 h-5 text-gray-500 transition-transform"
                  :class="{ 'transform rotate-180': expandedRoutes.digitalEntities }"
                />
              </button>
              <div v-show="expandedRoutes.digitalEntities" class="px-4 pb-4 border-t border-gray-200">
                <p class="text-gray-700 mt-3 mb-3">{{ $t('api.otherRoutes.digitalEntities.description') }}</p>
                <div class="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span class="font-medium text-gray-600">Rate limit:</span>
                    <span class="ml-2 text-gray-700">{{ $t('api.otherRoutes.digitalEntities.rateLimit') }}</span>
                  </div>
                  <div>
                    <span class="font-medium text-gray-600">Cache:</span>
                    <span class="ml-2 text-gray-700">{{ $t('api.otherRoutes.digitalEntities.cache') }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Projects -->
            <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                class="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                @click="expandedRoutes.projects = !expandedRoutes.projects"
              >
                <div class="flex items-center space-x-3">
                  <span class="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">GET</span>
                  <code class="text-sm font-mono text-gray-900">{{ $t('api.otherRoutes.projects.path') }}</code>
                  <span class="text-gray-700">{{ $t('api.otherRoutes.projects.title') }}</span>
                </div>
                <ChevronDownIcon 
                  class="w-5 h-5 text-gray-500 transition-transform"
                  :class="{ 'transform rotate-180': expandedRoutes.projects }"
                />
              </button>
              <div v-show="expandedRoutes.projects" class="px-4 pb-4 border-t border-gray-200">
                <p class="text-gray-700 mt-3 mb-3">{{ $t('api.otherRoutes.projects.description') }}</p>
                <div class="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span class="font-medium text-gray-600">Rate limit:</span>
                    <span class="ml-2 text-gray-700">{{ $t('api.otherRoutes.projects.rateLimit') }}</span>
                  </div>
                  <div>
                    <span class="font-medium text-gray-600">Cache:</span>
                    <span class="ml-2 text-gray-700">{{ $t('api.otherRoutes.projects.cache') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { API_URL } from '@/config';
import { ChevronDownIcon } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { tm } = useI18n();
const isLoading = ref(false);
const testResult = ref<{ success: boolean; count?: number; error?: string } | null>(null);
const isLoadingExportMappings = ref(false);
const exportMappingsTestResult = ref<{ success: boolean; count?: number; error?: string } | null>(null);

// État pour les accordéons des routes
const expandedRoutes = ref({
  schemas: false,
  exportMappings: false,
  tags: false,
  memos: false,
  agents: false,
  locations: false,
  materialEntities: false,
  digitalEntities: false,
  projects: false
});

const apiBaseUrl = computed(() => {
  return API_URL.replace(/\/$/, ''); // Retirer le slash final si présent
});

// Récupérer les items des cas d'usage
const useCasesItems = computed((): string[] => {
  const items = tm('api.endpoint.useCases.items') as unknown;
  return Array.isArray(items) ? (items as string[]) : [];
});

const testApi = async (): Promise<void> => {
  isLoading.value = true;
  testResult.value = null;
  
  try {
    const response = await fetch(`${apiBaseUrl.value}/public/schemas`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      testResult.value = {
        success: true,
        count: Array.isArray(data) ? data.length : 0
      };
    } else {
      testResult.value = {
        success: false,
        error: `Erreur ${response.status}: ${response.statusText}`
      };
    }
  } catch (error) {
    testResult.value = {
      success: false,
      error: error instanceof Error ? error.message : 'Erreur inconnue'
    };
  } finally {
    isLoading.value = false;
  }
};

const testExportMappingsApi = async (): Promise<void> => {
  isLoadingExportMappings.value = true;
  exportMappingsTestResult.value = null;

  try {
    const response = await fetch(`${apiBaseUrl.value}/export-mappings/active?skip=0&limit=5`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (response.ok) {
      const data = await response.json();
      exportMappingsTestResult.value = {
        success: true,
        count: Array.isArray(data) ? data.length : 0
      };
    } else {
      exportMappingsTestResult.value = {
        success: false,
        error: `Erreur ${response.status}: ${response.statusText}`
      };
    }
  } catch (error) {
    exportMappingsTestResult.value = {
      success: false,
      error: error instanceof Error ? error.message : 'Erreur inconnue'
    };
  } finally {
    isLoadingExportMappings.value = false;
  }
};
</script>

<style scoped>
code {
  font-family: 'Courier New', monospace;
}
</style>

